/** The failure taxonomy the backend actually produces.
 *
 * Callers branch on `kind`, never on a status number scattered through the UI.
 * The structured payload the backend sends is preserved verbatim, because it
 * carries the only information that makes a failure actionable: which issues
 * blocked a publish, which version the server is really on, which source views
 * are missing.
 */
export type ApiErrorKind =
  | "unauthenticated" // 401 — session absent or expired
  | "forbidden" // 403 — CSRF rejected
  | "not_found" // 404
  | "conflict" // 409 — stale version or lifecycle state
  | "validation" // 422 — structured rejection
  | "server" // 5xx
  | "network"; // request never completed

/** One structured issue, exactly as the backend serialises it. */
export interface ApiIssue {
  code: string;
  message: string;
  field: string | null;
  entity: string | null;
  meta: Record<string, unknown> | null;
  severity?: "blocking" | "warning";
}

/** The shapes the backend puts in `detail` for its structured failures. */
export interface ApiErrorDetail {
  error?: string;
  message?: string;
  issues?: ApiIssue[];
  /** stale_version (draft-scoped mutations) */
  current_version?: number;
  updated_at?: string | null;
  /** lifecycle conflicts */
  current_product_version?: number;
  current_status?: string | null;
  current_draft_version?: number | null;
  /** source_set_incomplete */
  missing_roles?: string[];
  present_roles?: string[];
  required_roles?: string[];
  variant_id?: string | null;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly detail: ApiErrorDetail | null;

  constructor(
    kind: ApiErrorKind,
    status: number,
    message: string,
    detail: ApiErrorDetail | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }

  /** The backend's own error code, when it sent one. */
  get code(): string | null {
    return typeof this.detail?.error === "string" ? this.detail.error : null;
  }

  get issues(): ApiIssue[] {
    return Array.isArray(this.detail?.issues) ? this.detail.issues : [];
  }

  /** The account is refused because of what it MAY DO, not because of CSRF.
   *
   * Both are 403 and they need opposite copy. A CSRF token that no longer
   * matches is fixed by reloading; a missing capability is not fixed by
   * anything the operator can do at the keyboard, and telling them to reload
   * sends them round a loop that always ends in the same place. The backend
   * separates the two by sending a structured `forbidden` code for the
   * authorization refusal and a bare string for the CSRF one.
   */
  get isAuthorizationDenied(): boolean {
    return this.kind === "forbidden" && this.code === "forbidden";
  }

  /** The account is still holding a password somebody else generated.
   *
   * Every business route answers this until it is replaced, so it is a
   * navigation instruction rather than an error: the operator is sent to the
   * password screen, which is the one place that can end it. */
  get requiresPasswordChange(): boolean {
    return this.code === "password_change_required";
  }

  /** The current password supplied to confirm a security change was wrong. */
  get reauthenticationFailed(): boolean {
    return this.code === "reauthentication_failed";
  }

  /** True when the caller should reload server state before retrying. */
  get isStale(): boolean {
    return (
      this.kind === "conflict" &&
      (this.code === "stale_version" ||
        this.code === "stale_product_version" ||
        this.code === "stale_draft_version")
    );
  }

  /** A sentence safe to show an administrator. Never a stack trace.
   *
   * The backend's own message is used only for the failures it writes for a
   * human reader — validation, conflicts and missing resources. A 5xx body is
   * not written for anyone's eyes and can carry internal detail, so those
   * always get the generic sentence.
   */
  get userMessage(): string {
    const detail = this.detail;
    const backendWrote =
      this.kind === "validation" ||
      this.kind === "conflict" ||
      this.kind === "not_found";
    if (backendWrote && detail && typeof detail.message === "string" && detail.message) {
      return detail.message;
    }
    switch (this.kind) {
      case "unauthenticated":
        return "Your session has expired. Please sign in again.";
      case "forbidden":
        // Three different 403s, and they need three different sentences. Only
        // the last one — a CSRF token that no longer matches the session — is
        // actually fixed by reloading, and telling the other two to reload
        // sends an operator round a loop that ends where it started.
        if (this.requiresPasswordChange) {
          return "Set your own password before using the dashboard.";
        }
        if (this.isAuthorizationDenied) {
          return "You don't have permission to perform this action.";
        }
        if (this.reauthenticationFailed && typeof detail?.message === "string") {
          return detail.message;
        }
        return "That request was rejected for security reasons. Reload the page and try again.";
      case "not_found":
        return "That item no longer exists.";
      case "conflict":
        return "This product changed somewhere else. Reload to see the current state.";
      case "validation":
        return "Some details need attention before this can be saved.";
      case "server":
        return "The server could not complete that request. Try again in a moment.";
      case "network":
        return "The server could not be reached. Check that the backend is running.";
    }
  }
}

export function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422) return "validation";
  return "server";
}
